import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import httpService from "../services/httpService";
import posthog from 'posthog-js';
import { useUiStore } from "../store/store";

import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import Badge from "react-bootstrap/Badge";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

import { FaListUl, FaComments, FaBullseye } from "react-icons/fa";

import LoadingFade from "../components/LoadingFade";
import PodcastClubChat from "../components/Chat/PodcastClubChat";
import ListenerSkills from "../components/ListenerComponents/ListenerSkills";
import ClubHeroCard from "../components/ListenerComponents/ClubHeroCard";
import NewHereSection from "../components/ListenerComponents/NewHereSection";
import ClubEpisodeRow from "../components/ListenerComponents/ClubEpisodeRow";
import AboutClubModal from "../components/ListenerComponents/AboutClubModal";

import "../styles/dashbaords.scss";
import "../styles/ListenerPodcastClub.scss";


const ListenerPodcastClub = () => {
  const { guid } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [podcastClub, setPodcastClub] = useState({});
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showAbout, setShowAbout] = useState(false);

  const listenerSubscribedToClub    = useUiStore((s) => s.listenerSubscribedToClub);
  const setListenerSubscribedToClub = useUiStore((s) => s.setListenerSubscribedToClub);

  useEffect(() => {
    if (guid) loadPodcastClub();
  }, [guid]);

  useEffect(() => {
    if (!listenerSubscribedToClub) return;
    httpService.get('/dash/listener/subscription-count')
      .then((res) => {
        if (res.data.count <= 1) setShowWelcomeModal(true);
      })
      .catch(() => {});
  }, [listenerSubscribedToClub]);

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
    setListenerSubscribedToClub(false);
  };

  const loadPodcastClub = () => {
    setError(null);
    httpService.get(`/dash/listener-podcast-club/${guid}`)
      .then((res) => {
        setPodcastClub(res.data.podcast_club);
        posthog.capture('listener_podcast_club_viewed', {
          club_guid: guid,
          club_title: res.data.podcast_club?.title ?? null,
        });
      })
      .catch((err) => setError(err.response?.data?.message || "Error loading podcast club"))
      .finally(() => setLoading(false));
  };

  const episodes = podcastClub.episodes || [];
  const hasEngaged = !!podcastClub.has_engaged;
  // "Start here": only the first released episode, only while nothing in the club has been engaged with.
  const firstReleasedIdx = episodes.findIndex((e) => e.released);

  const openAbout = () => {
    setShowAbout(true);
    posthog.capture('club_about_opened', { club_guid: guid });
  };

  return (
    <>
    <LoadingFade id="ListenerPodcastClub-component" loading={loading} stateKey={loading ? 'loading' : 'loaded'}>

      {error && <Alert variant="danger" className="m-3">{error}</Alert>}

      {!error && !loading && (
        <div className="lpc-page">
          <ClubHeroCard club={podcastClub} onAbout={openAbout} />
          <NewHereSection key={guid} clubGuid={guid} hasEngaged={hasEngaged} />

          <Tabs defaultActiveKey="episodes" id="podcast-club-tabs" className="mb-3">

            <Tab
              eventKey="episodes"
              title={
                <span>
                  <FaListUl className="me-1" />
                  Episodes
                  {episodes.length > 0 && (
                    <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.62rem' }}>
                      {episodes.length}
                    </Badge>
                  )}
                </span>
              }
            >
              {episodes.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaListUl style={{ fontSize: '2.5rem', opacity: 0.2 }} />
                  <p className="mt-3 mb-0">No episodes available yet.</p>
                </div>
              ) : (
                <div className="lpc-list">
                  {episodes.map((episode, idx) => {
                    let badge = null;
                    if (episode.released && !episode.engaged) {
                      badge = (!hasEngaged && idx === firstReleasedIdx) ? 'start' : 'new';
                    }
                    return (
                      <ClubEpisodeRow
                        key={episode.id}
                        episode={episode}
                        number={idx + 1}
                        badge={badge}
                        clubGuid={guid}
                      />
                    );
                  })}
                </div>
              )}
            </Tab>

            <Tab
              eventKey="chat"
              title={<span><FaComments className="me-1" />Chat with Podcaster</span>}
            >
              <PodcastClubChat clubGuid={guid} />
            </Tab>

            <Tab
              eventKey="skills"
              title={<span><FaBullseye className="me-1" />Your Skills</span>}
            >
              <ListenerSkills clubGuid={guid} />
            </Tab>

          </Tabs>
        </div>
      )}

    </LoadingFade>

    <AboutClubModal
      show={showAbout}
      onClose={() => setShowAbout(false)}
      clubTitle={podcastClub.title}
      hostName={podcastClub.host_name}
    />

    <Modal show={showWelcomeModal} onHide={handleCloseWelcomeModal} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Welcome to your first Podcast Club!</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
          <iframe
            src="https://www.loom.com/embed/e0ce06042c544477a7c0ad20ec55c7f4?autoplay=1"
            frameBorder="0"
            webkitallowfullscreen="true"
            mozallowfullscreen="true"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            title="Welcome to Podsy"
          />
        </div>
      </Modal.Body>
    </Modal>
    </>
  );
};

export default ListenerPodcastClub;
